/*
 * Copyright 2020-2021 AskChristee, LLC. All rights reserved.
 */

import React from 'react';

import { TBuyerQualChoiceResponseObjectV2 } from '../../../../types/api';
import { BrowMess, DetailCashNaked, DetailPercent, DetailText, DetailTextCenter, HtmlDetailTextTwo, TdDownloadPdf } from '../../../atoms';

interface Props {
  conventional: TBuyerQualChoiceResponseObjectV2;
  fha: TBuyerQualChoiceResponseObjectV2;
  va: TBuyerQualChoiceResponseObjectV2;
  usda: TBuyerQualChoiceResponseObjectV2;
}

const BuyerChoice = ({ conventional, fha, va, usda }: Props) => {
  if (conventional.aCalc) {
    var aText = conventional.aCalc.brow_head;
  } else {
    var aText = '';
  }
  if (fha.aCalc) {
    var bText = fha.aCalc.brow_head;
  } else {
    var bText = '';
  }
  if (va.aCalc) {
    var cText = va.aCalc.brow_head;
  } else {
    var cText = '';
  }
  if (usda.aCalc) {
    var dText = usda.aCalc.brow_head;
  } else {
    var dText = '';
  }

  return (
    <table className="table">
      <tbody>
        <tr>
          <td colSpan={5}>
            <BrowMess a={aText} b={bText} c={cText} d={dText} />
          </td>
        </tr>
        <tr>
          <DetailText v="Loan Type" />
          {conventional?.aCalc ? <DetailTextCenter v={conventional.aCalc.report_type} /> : null}
          {fha?.aCalc ? <DetailTextCenter v={fha.aCalc.report_type} /> : null}
          {va?.aCalc ? <DetailTextCenter v={va.aCalc.report_type} /> : null}
          {usda?.aCalc ? <DetailTextCenter v={usda.aCalc.report_type} /> : null}
        </tr>
        <tr>
          <DetailText v="Alerts" />
          {conventional?.aCalc ? <DetailTextCenter v={conventional?.aCalc?.fumess} /> : null}
          {fha?.aCalc ? <DetailTextCenter v={fha?.aCalc?.fumess} /> : null}
          {va?.aCalc ? <DetailTextCenter v={va?.aCalc?.fumess} /> : null}
          {usda?.aCalc ? <DetailTextCenter v={usda?.aCalc?.fumess} /> : null}
        </tr>
        <tr>
          <DetailText v="Target Monthly Payment" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.target_piti} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.target_piti} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.target_piti} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.target_piti} /> : null}
        </tr>
        <tr className="active-row">
          <DetailText v="Purchase Price" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.sp} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.sp} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.sp} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.sp} /> : null}
        </tr>
        <tr className="active-row">
          <DetailText v="Base Loan Amount" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc?.ma} b={conventional?.aCalc?.ltv} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc?.ma} b={fha?.aCalc?.ltv} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc?.ma} b={va?.aCalc?.ltv} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc?.ma} b={usda?.aCalc?.ltv} /> : null}
        </tr>
        <tr>
          <DetailText v="Total Loan Amount" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.total_mtge} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.total_mtge} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.total_mtge} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.total_mtge} /> : null}
        </tr>
        <tr>
          <DetailText v="Down Payment" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc?.d_payment} b={conventional?.aCalc?.pdf_dp} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc?.d_payment} b={fha?.aCalc?.pdf_dp} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc?.d_payment} b={va?.aCalc?.pdf_dp} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc?.d_payment} b={usda?.aCalc?.pdf_dp} /> : null}
        </tr>
        <tr>
          <DetailText v="Closing Cost" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.totalcc} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.totalcc} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.totalcc} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.totalcc} /> : null}
        </tr>
        <tr>
          <DetailText v="Seller Credit" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional.aCalc.dol_cred} b={conventional.aCalc.temp_cred} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha.aCalc.dol_cred} b={fha.aCalc.temp_cred} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va.aCalc.dol_cred} b={va.aCalc.temp_cred} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda.aCalc.dol_cred} b={usda.aCalc.temp_cred} /> : null}
        </tr>
        <tr>
          <DetailText v="Lender Credit" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc?.dol_lend} b={conventional?.aCalc?.temp_lend} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc?.dol_lend} b={fha?.aCalc?.temp_lend} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc?.dol_lend} b={va?.aCalc?.temp_lend} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc?.dol_lend} b={usda?.aCalc?.temp_lend} /> : null}
        </tr>
        <tr className="active-row">
          <DetailText v="Required Cash" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.r_cash} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.r_cash} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.r_cash} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.r_cash} /> : null}
        </tr>
        <tr>
          <DetailText v="Cash available" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.t_cash} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.t_cash} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.t_cash} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.t_cash} /> : null}
        </tr>
        <tr>
          <DetailText v="Additional Cash Required" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.ex_cash} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.ex_cash} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.ex_cash} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.ex_cash} /> : null}
        </tr>
        <tr>
          <DetailText v="Interest Rate" />
          {conventional.aCalc && <DetailTextCenter v={conventional.aCalc.rate_display.toString()} />}
          {fha.aCalc && <DetailTextCenter v={fha.aCalc.rate_display.toString()} />}
          {va.aCalc && <DetailTextCenter v={va.aCalc.rate_display.toString()} />}
          {usda.aCalc && <DetailTextCenter v={usda.aCalc.rate_display.toString()} />}
        </tr>
        <tr className="active-row">
          <DetailText v="Total Monthly Payment" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.t_piti} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.t_piti} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.t_piti} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.t_piti} /> : null}
        </tr>
        <tr>
          <DetailText v="Principal &amp; Interest" />
          {conventional.aCalc && <HtmlDetailTextTwo a={conventional.aCalc.temp_pi.toString()} b={conventional.aCalc.res_pi.toString()} />}
          {fha.aCalc && <HtmlDetailTextTwo a={fha.aCalc.temp_pi.toString()} b={fha.aCalc.res_pi.toString()} />}
          {va.aCalc && <HtmlDetailTextTwo a={va.aCalc.temp_pi.toString()} b={va.aCalc.res_pi.toString()} />}
          {usda.aCalc && <HtmlDetailTextTwo a={usda.aCalc.temp_pi.toString()} b={usda.aCalc.res_pi.toString()} />}
        </tr>
        <tr>
          <DetailText v="Monthly Mortgage Insurance" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc.temp_mi} b={conventional?.aCalc.res_mi} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc.temp_mi} b={fha?.aCalc.res_mi} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc.temp_mi} b={va?.aCalc.res_mi} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc.temp_mi} b={usda?.aCalc.res_mi} /> : null}
        </tr>
        <tr>
          <DetailText v="Monthly Property Taxes" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc.temp_pt} b={conventional?.aCalc.res_pt} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc.temp_pt} b={fha?.aCalc.res_pt} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc.temp_pt} b={va?.aCalc.res_pt} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc.temp_pt} b={usda?.aCalc.res_pt} /> : null}
        </tr>
        <tr>
          <DetailText v="Monthly Property Insurance" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc.temp_ins} b={conventional?.aCalc.res_ins} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc.temp_ins} b={fha?.aCalc.res_ins} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc.temp_ins} b={va?.aCalc.res_ins} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc.temp_ins} b={usda?.aCalc.res_ins} /> : null}
        </tr>
        <tr>
          <DetailText v="Monthly Condo/HOA fee" />
          {conventional?.aCalc ? <HtmlDetailTextTwo a={conventional?.aCalc.condo} b={conventional?.aCalc.res_condo} /> : null}
          {fha?.aCalc ? <HtmlDetailTextTwo a={fha?.aCalc.condo} b={fha?.aCalc.res_condo} /> : null}
          {va?.aCalc ? <HtmlDetailTextTwo a={va?.aCalc.condo} b={va?.aCalc.res_condo} /> : null}
          {usda?.aCalc ? <HtmlDetailTextTwo a={usda?.aCalc.condo} b={usda?.aCalc.res_condo} /> : null}
        </tr>
        <tr>
          <DetailText v="Estimated Annual Tax Deduction" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.tot_duct} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.tot_duct} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.tot_duct} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.tot_duct} /> : null}
        </tr>
        <tr>
          <DetailText v="Net Monthly Mortgage Payment" />
          {conventional?.aCalc ? <DetailCashNaked v={conventional?.aCalc?.net_mort_pay} /> : null}
          {fha?.aCalc ? <DetailCashNaked v={fha?.aCalc?.net_mort_pay} /> : null}
          {va?.aCalc ? <DetailCashNaked v={va?.aCalc?.net_mort_pay} /> : null}
          {usda?.aCalc ? <DetailCashNaked v={usda?.aCalc?.net_mort_pay} /> : null}
        </tr>
        <tr>
          <DetailText v="Debt Ratio" />
          {conventional?.aCalc ? <DetailPercent v={conventional?.aCalc?.cal_br} /> : null}
          {fha?.aCalc ? <DetailPercent v={fha?.aCalc?.cal_br} /> : null}
          {va?.aCalc ? <DetailPercent v={va?.aCalc?.cal_br} /> : null}
          {usda?.aCalc ? <DetailPercent v={usda?.aCalc?.cal_br} /> : null}
        </tr>
        <tr>
          <TdDownloadPdf />
        </tr>
      </tbody>
    </table>
  );
};

export default BuyerChoice;
